import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button, Checkbox, LoadingIcon, Wrapper } from '@pkg/components';
import { GameRow } from '@pkg/components/Sections/Admin/Fantasy';
import { GameRowActions } from '@pkg/components/Sections/Admin/Fantasy/GameList/GameRowActions';
import {
    DeleteWrapper,
    GameTable,
    GameTableItem,
    ListTable,
} from '@pkg/components/Sections/Admin/Fantasy/GameList/styles';
import { Pagination } from '@pkg/components/Sections/Admin/components/Pagination';
import { type SortParams, useBreakpoints, useDeleteCompetition, useGetAdminCompetitions } from '@pkg/hooks';

import { HeadingTitle } from '@app/pages/FantasyWishlist/components/CollectionTable/styles';

const headerFields = ['name', 'dates', 'maximum_values', 'info'];

export const GameList = () => {
    const { t } = useTranslation('admin_fantasy');
    const { isMobile } = useBreakpoints();
    const [checkedIds, setCheckedIds] = useState<string[]>([]);
    const { mutate: deleteGames } = useDeleteCompetition();
    const [sortParams, setSortParams] = useState<SortParams>({
        sortField: 'submission_starts_at',
        sortDirectionQuery: 'Desc',
    });
    const { games, hasNextPage, fetchNextPage, fetchPreviousPage, pagingParams, isFetching, isLoading } =
        useGetAdminCompetitions({
            sortDirectionQuery: sortParams?.sortDirectionQuery,
            sortField: sortParams?.sortField,
        });

    const hasPreviousPage = Boolean(pagingParams?.page && pagingParams.page > 0);

    function handleChecked(id: string) {
        setCheckedIds((c) => {
            if (c.includes(id)) {
                return c.filter((cId) => cId !== id);
            } else {
                return [...c, id];
            }
        });
    }

    const handleDelete = useCallback(async () => {
        const confirmDelete = confirm(`🚨 🚨 🚨 ARE YOU SURE YOU WANT TO DELETE ${checkedIds?.length} GAMES?????`);
        if (checkedIds && confirmDelete) {
            await Promise.all(checkedIds.map((checkedId) => deleteGames(checkedId))).then(() => {
                alert('successfully deleted the games!');
                setCheckedIds([]);
            });
        }
    }, [checkedIds, deleteGames]);
    function handleSort(heading: string) {
        let sortField = heading;
        if (heading === 'dates') sortField = 'submission_starts_at';
        if (heading === 'maximum_values') sortField = 'max_value';

        setSortParams((c) => ({
            sortField: sortField,
            sortDirectionQuery: c.sortDirectionQuery === 'Desc' ? 'Asc' : 'Desc',
        }));
    }

    const paginationMarkup = !isLoading && !isFetching && (
        <Pagination
            variant="buttoned"
            fetchNextPage={fetchNextPage}
            fetchPreviousPage={fetchPreviousPage}
            isFetching={isFetching}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            pagingParams={pagingParams}
        />
    );

    const gameListMarkup = games?.map((game) => {
        const checked = !!checkedIds?.find((id) => id === game.id);
        return (
            <ListTable key={`${game.id}-${game.name}`}>
                <GameRow gameData={game} />
                <GameRowActions gameData={game} />
                <Checkbox
                    onChange={() => {
                        handleChecked(game.id);
                    }}
                    checked={checked}
                    id={game.id}
                />
            </ListTable>
        );
    });

    if (isFetching || isLoading) {
        return <LoadingIcon />;
    }

    return (
        <Wrapper>
            {paginationMarkup}
            {!isMobile && (
                <ListTable $isHeader>
                    <GameTable>
                        {headerFields.map((heading) => {
                            const headingActive =
                                sortParams.sortField === 'submission_starts_at'
                                    ? heading === 'dates'
                                    : sortParams.sortField === 'max_value'
                                      ? heading === 'maximum_values'
                                      : heading === sortParams.sortField;
                            return (
                                <GameTableItem $isHeader key={heading} onClick={() => handleSort(heading)}>
                                    <HeadingTitle
                                        $isActive={headingActive}
                                        $isDesc={sortParams.sortDirectionQuery === 'Desc'}
                                    >
                                        {t(`list_item_header_${heading}`)}
                                    </HeadingTitle>
                                </GameTableItem>
                            );
                        })}
                    </GameTable>
                    <GameTableItem $isHeader></GameTableItem>
                </ListTable>
            )}
            {checkedIds?.length ? (
                <DeleteWrapper>
                    <Button
                        size="xsmall"
                        color="transparent"
                        onClick={handleDelete}
                    >{`Delete selected (${checkedIds.length})`}</Button>
                </DeleteWrapper>
            ) : null}
            {gameListMarkup}
            {paginationMarkup}
        </Wrapper>
    );
};
